import React from 'react';
import './Loader.css';

interface LoaderProps {
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'white';
  fullPage?: boolean;
  text?: string;
  overlay?: boolean;
  className?: string;
  variant?: 'spinner' | 'pulse' | 'dots';
}

const Loader: React.FC<LoaderProps> = ({
  size = 'medium',
  color = 'primary',
  fullPage = false,
  text,
  overlay = false,
  className = '',
  variant = 'spinner'
}) => {
  // Size mapping
  const sizeMap = {
    small: 'h-6 w-6 border-2',
    medium: 'h-10 w-10 border-2', 
    large: 'h-16 w-16 border-3',
  };

  // Determine container classes
  const containerClasses = fullPage
    ? 'fixed inset-0 flex items-center justify-center bg-gray-50 bg-opacity-90 z-50 loader-fullpage'
    : overlay 
      ? 'absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10 loader-overlay'
      : 'flex flex-col items-center justify-center';

  // Render the appropriate loader variant
  const renderLoader = () => {
    switch (variant) {
      case 'pulse':
        return (
          <div className={`
            loader-pulse 
            size-${size} 
            color-${color}
          `}>
            <div className={`
              loader-spinner 
              spinner-${color} 
              ${sizeMap[size]}
            `}></div>
          </div>
        );
      
      case 'dots':
        return (
          <div className="flex space-x-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`
                  rounded-full 
                  ${color === 'primary' ? 'bg-blue-600' : color === 'secondary' ? 'bg-gray-600' : 'bg-white'}
                  ${size === 'small' ? 'w-2 h-2' : size === 'medium' ? 'w-3 h-3' : 'w-4 h-4'}
                `}
                style={{
                  animation: `fadeInOut 1.5s ease-in-out ${i * 0.2}s infinite`
                }}
              ></div>
            ))}
          </div>
        );
      
      default: // spinner
        return (
          <div 
            className={`
              loader-spinner 
              spinner-${color} 
              ${sizeMap[size]}
            `}
            aria-label="Loading" 
          />
        );
    }
  };

  return (
    <div className={`${containerClasses} ${className}`}>
      <div className="flex flex-col items-center">
        {renderLoader()}
        
        {text && (
          <p className={`
            mt-4 font-medium loader-text
            ${color === 'white' ? 'text-white' : 'text-gray-700'}
            ${size === 'small' ? 'text-sm' : size === 'large' ? 'text-lg' : 'text-base'}
          `}>
            {text}
          </p>
        )}
      </div>
    </div>
  );
};

export default Loader; 